import { useState, useEffect, useRef } from "react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Sparkles, Check, X } from "lucide-react"
import { useGenerateContainerDescription, useUpdateContainer } from "@/hooks/useResources"
import { useQueryClient } from "@tanstack/react-query"
import { useToast } from "@/hooks/useToast"
import { ResourceContainer } from "@/types/api"

interface ContainerDescriptionProps {
  resourceId: string
  container: ResourceContainer | null
}

export function ContainerDescription({ resourceId, container }: ContainerDescriptionProps) {
  const [containerDescription, setContainerDescription] = useState("")
  const [isDescriptionChanged, setIsDescriptionChanged] = useState(false)
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false)
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const magicWandButtonRef = useRef<HTMLButtonElement>(null)

  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { refetch: refetchDescription } = useGenerateContainerDescription(resourceId, container?.id || "")

  const updateContainerMutation = useUpdateContainer({
    onSuccess: () => {
      toast({
        title: "Description updated",
        description: "Container description has been updated successfully.",
      })
      queryClient.invalidateQueries({
        queryKey: ["resources/containers", resourceId],
      })
      setIsDescriptionChanged(false)
      setIsEditingDescription(false)
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update description. Please try again.",
        variant: "destructive",
      })
    },
  })

  // Update container description when container changes
  useEffect(() => {
    if (container) {
      // Get description from meta if available
      const meta = (container as any).meta
      const description = meta?.description || ""
      setContainerDescription(description)
      setIsDescriptionChanged(false)
      setIsEditingDescription(false)
    }
  }, [container])

  if (!container) {
    return null
  }

  return (
    <div className="mt-2">
      {isEditingDescription ? (
        <div className="flex items-start gap-2">
          <div className="relative flex-1">
            <Textarea
              value={containerDescription}
              onChange={(e) => {
                setContainerDescription(e.target.value)
                setIsDescriptionChanged(e.target.value !== ((container as any).meta?.description || ""))
              }}
              onBlur={(e) => {
                // Don't exit edit mode if clicking on the magic wand button
                const relatedTarget = e.relatedTarget as HTMLElement
                if (
                  relatedTarget === magicWandButtonRef.current ||
                  magicWandButtonRef.current?.contains(relatedTarget)
                ) {
                  return
                }
                if (!isDescriptionChanged) {
                  setIsEditingDescription(false)
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  // Reset to original value
                  const meta = (container as any).meta
                  const originalDescription = meta?.description || ""
                  setContainerDescription(originalDescription)
                  setIsDescriptionChanged(false)
                  setIsEditingDescription(false)
                } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault()
                  if (isDescriptionChanged && resourceId && container) {
                    updateContainerMutation.mutate({
                      resourceId: resourceId,
                      containerId: container.id,
                      data: { description: containerDescription },
                    })
                    setIsEditingDescription(false)
                  }
                }
              }}
              placeholder="Add a description for this table..."
              className="min-h-[70px] resize-none text-sm"
              autoFocus
            />
            <div className="absolute bottom-2 right-2">
              <Button
                ref={magicWandButtonRef}
                size="sm"
                variant="ghost"
                onMouseDown={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
                onClick={async (e) => {
                  e.stopPropagation()
                  if (!resourceId || !container) return
                  setIsGeneratingDescription(true)
                  try {
                    const result = await refetchDescription()
                    if (result.data) {
                      setContainerDescription(result.data)
                      setIsDescriptionChanged(true)
                    }
                  } catch {
                    toast({
                      title: "Error",
                      description: "Failed to generate description. Please try again.",
                      variant: "destructive",
                    })
                  } finally {
                    setIsGeneratingDescription(false)
                  }
                }}
                disabled={isGeneratingDescription}
                className="h-6 w-6 p-0 hover:bg-transparent"
              >
                {isGeneratingDescription ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600 dark:border-gray-600 dark:border-t-gray-300" />
                ) : (
                  <Sparkles className="h-4 w-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" />
                )}
              </Button>
            </div>
          </div>
          <div className="flex flex-shrink-0 flex-col gap-2">
            <Button
              size="xs"
              variant="outline"
              onClick={() => {
                // Reset to original value and exit edit mode
                const meta = (container as any).meta
                const originalDescription = meta?.description || ""
                setContainerDescription(originalDescription)
                setIsDescriptionChanged(false)
                setIsEditingDescription(false)
              }}
              className="h-8 px-3"
            >
              <X className="mr-1 h-4 w-4 stroke-gray-800 dark:stroke-gray-200" />
              Cancel
            </Button>
            {isDescriptionChanged && (
              <Button
                size="xs"
                variant="default"
                onClick={() => {
                  if (!resourceId || !container) return
                  updateContainerMutation.mutate({
                    resourceId: resourceId,
                    containerId: container.id,
                    data: { description: containerDescription },
                  })
                  setIsEditingDescription(false)
                }}
                className="h-8 px-3"
              >
                <Check className="mr-1 h-4 w-4 stroke-gray-200 dark:stroke-gray-800" />
                Save
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div onClick={() => setIsEditingDescription(true)} className="group relative w-3/4 cursor-text">
          {containerDescription ? (
            <p className="text-sm text-gray-600 group-hover:text-gray-900 dark:text-gray-400 dark:group-hover:text-gray-200">
              {containerDescription}
            </p>
          ) : (
            <p className="text-sm text-gray-400 group-hover:text-gray-600 dark:text-gray-500 dark:group-hover:text-gray-400">
              Add a description for this table...
            </p>
          )}
        </div>
      )}
    </div>
  )
}
